import { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';
import { useLanguage } from '../lib/i18n';
import { formatCurrency } from '../lib/currency';
import { checkoutApi } from '../api/client';

export default function OrderConfirmation() {
  const { orderNumber } = useParams();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { user } = useAuth();
  const { locale } = useLanguage();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!orderNumber) return;
    // Guests need the access token; logged-in owners / admins do not.
    checkoutApi.getOrder(orderNumber, token)
      .then(r => setOrder(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [orderNumber, token]);

  if (loading) return (
    <div className="max-w-2xl mx-auto px-6 py-24 text-center">
      <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin mx-auto" />
    </div>
  );

  return (
    <div data-testid="order-confirmation" className="max-w-2xl mx-auto px-6 md:px-12 py-12">
      <div className="text-center mb-10">
        <div className="w-16 h-16 rounded-full bg-primary flex items-center justify-center mx-auto mb-5 shadow-sm">
          <CheckCircle className="w-8 h-8 text-primary-foreground" />
        </div>
        <h1 className="font-serif text-4xl font-light text-foreground mb-2">Bedankt voor uw bestelling</h1>
        {order && (
          <p className="text-muted-foreground">Bestelnummer <span data-testid="order-number" className="font-mono font-semibold text-foreground">#{order.orderNumber}</span></p>
        )}
      </div>

      {order && (
        <div className="bg-card border border-border rounded-2xl overflow-hidden mb-8 shadow-sm">
          <div className="px-6 py-4 border-b border-border bg-muted/50">
            <h2 className="font-serif text-lg font-light text-foreground">Besteldetails</h2>
          </div>
          {order.lines?.length > 0 ? (
            <div className="divide-y divide-border">
              {order.lines.map(line => {
                const name = line[`nameSnapshot_${locale}`] || line.nameSnapshot_nl;
                return (
                  <div key={line._id} className="flex items-center justify-between px-6 py-4 text-sm">
                    <span className="text-foreground">{name} <span className="text-muted-foreground">x {line.quantity}</span></span>
                    <span className="font-semibold">{formatCurrency(line.priceSnapshot * line.quantity, locale)}</span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="px-6 py-4 text-sm text-muted-foreground">Orderregels worden verwerkt...</p>
          )}
          <div className="px-6 py-4 border-t border-border flex items-center justify-between">
            <span className="font-serif text-base text-foreground">Totaal</span>
            <span data-testid="order-total" className="font-semibold">{formatCurrency(order.totalAmount, locale)}</span>
          </div>
          {order.deliveryAddressSnapshot && (
            <div className="px-6 py-4 border-t border-border">
              <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold mb-2">Leveringsadres</p>
              <p className="text-sm text-foreground">
                {[order.deliveryAddressSnapshot.street, order.deliveryAddressSnapshot.number].filter(Boolean).join(' ')}
                {order.deliveryAddressSnapshot.bus ? ` bus ${order.deliveryAddressSnapshot.bus}` : ''}
              </p>
              <p className="text-sm text-foreground">{order.deliveryAddressSnapshot.postalCode} {order.deliveryAddressSnapshot.city}</p>
              <p className="text-sm text-foreground">{order.deliveryAddressSnapshot.country}</p>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <Link to="/catalog" className="flex-1 text-center py-3 border border-border rounded-full text-sm font-medium hover:bg-muted transition-colors">
          Verder winkelen
        </Link>
        {user && (
          <Link to="/account" className="flex-1 text-center py-3 bg-primary text-primary-foreground rounded-full text-sm font-semibold hover:bg-primary/90 transition-all active:scale-95">
            Mijn bestellingen
          </Link>
        )}
      </div>
    </div>
  );
}
